import { useEffect, useState } from "react";

/**
 * A shared "as of" instant that advances once per `intervalMs`.
 *
 * Ticks are aligned to wall-clock multiples of the interval, so every
 * consumer that uses the same interval re-renders in the same frame and
 * relative labels ("3m ago", "due in 2h") change together. Between ticks the
 * returned `Date` is referentially stable, so it is safe as a memo or
 * `React.memo` input. Prefer this over `new Date()` in render: a fresh Date
 * per render defeats memoization for every consumer below it.
 */
export function useNow(intervalMs: number): Date {
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined;
        const schedule = (): void => {
            const wait = intervalMs - (Date.now() % intervalMs);
            timer = setTimeout(() => {
                setNow(new Date());
                schedule();
            }, wait);
        };
        schedule();
        return () => {
            if (timer !== undefined) clearTimeout(timer);
        };
    }, [intervalMs]);

    return now;
}
